import {Component, OnInit} from '@angular/core';
import {Subject, timer} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {ToastService} from "../../core/services/toast.service";
import {HttpClientService} from "../../core/services/http-client.service";
import {CommonService} from "../../core/services/common.service";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.css'
})
export class SignUpComponent implements OnInit{
  Math = Math; // Make Math available in template
  user: any = {
    mobile: null,
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    otpCode: null,
    referenceId: null,
    isSmartSeller: true
  };

  otp: string[] = ['', '', '', '', ''];
  showPassword = false;
  passwordStrength = 0;
  hasLowerCase = false;
  hasUpperCase = false;
  hasNumber = false;
  hasMinLength = false;
  smartSellerFlag = false;
  wantsToBeSmartSeller = true;
  isLoading = false;
  isCheckingMobile = false;
  showOtpSection = false;
  mobileVerified = false;
  otpTimer = 0;
  timerSubscription: any;
  customer: any;

  constructor(
    private toast: ToastService,
    private httpService: HttpClientService,
    private common: CommonService,
    private route: ActivatedRoute,
    private router: Router
  ) {this.customer = this.common.getStorage('customer');}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.smartSellerFlag = params.isSmartSeller === 'true';
      this.user.isSmartSeller = this.smartSellerFlag;

      // Prefill mobile if coming from another flow
      if (params.mobile) {
        this.user.mobile = params.mobile;
      }
    });
    console.log("RegisterComponent ngOnInit",document.referrer);
  }

  checkMobile() {
    if (!this.user.mobile || this.user.mobile.toString().length !== 10) {
      this.toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    this.isCheckingMobile = true;
    const params = { isSmartSeller: true };
    this.httpService.get(`store/checkmobile/${this.user.mobile}`,params).subscribe(
      (res) => {
        this.isCheckingMobile = false;
        if (res.responseStatus.toLowerCase() === 'success') {
          if (res.response.exists) {
            // Mobile exists - redirect to login
            this.toast.info('Account already exists. Please login.');
            this.router.navigate(['/login'], {
              queryParams: { customerId: res.response.customer.id }
            });
          } else {
            // Mobile doesn't exist - request OTP
            this.requestOtp();
          }
        } else {
          this.toast.error('Server error');
        }
      },
      (error) => {
        this.isCheckingMobile = false;
        this.toast.error('Error checking mobile number');
      }
    );
  }

  requestOtp() {
    const params = { mobile: this.user.mobile };
    this.httpService.get("store/otp/generateOTP", params).subscribe(
      (response) => {
        if (response.responseStatus.toLowerCase() === 'success') {
          this.user.referenceId = response.response.reference_id;
          this.showOtpSection = true;
          this.startOtpTimer();
          this.toast.success('OTP sent to your mobile');
        } else {
          this.toast.error('Failed to send OTP');
        }
      },
      (error) => {
        this.toast.error('Error sending OTP');
      }
    );
  }

  verifyOtp() {
    if (!this.isOtpComplete()) {
      this.toast.error('Please enter the complete OTP');
      return;
    }

    this.user.otpCode = this.otp.join('');
    const params = {
      mobile: this.user.mobile,
      otpCode: this.user.otpCode,
      referenceId: this.user.referenceId,
    };

    this.httpService.get("otp/verifyOTP", params).subscribe(
      (response) => {
        if (response.responseStatus.toLowerCase() === 'success' && response.response.result) {
          const customer = response.response.customer;
          this.common.setStorage('customer', customer);
          this.customer = customer;
          this.common.setLoggedIn(true);
          // OTP verified
          this.mobileVerified = true;
          this.showOtpSection = false;
          this.toast.success('Mobile number verified successfully!');
        } else {
          this.toast.error('Invalid OTP. Please try again.');
        }
      },
      (error) => {
        this.toast.error('Error verifying OTP');
      }
    );
  }

  startOtpTimer() {
    this.otpTimer = 120; // 2 minutes
    this.timerSubscription = timer(0, 1000).subscribe(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.timerSubscription.unsubscribe();
      }
    });
  }

  onOtpKeyUp(event: any, index: number) {
    const input = event.target;
    const value = input.value;

    // Auto move to next input
    if (value && index < 4) {
      const nextInput = document.querySelector(`input[name=otp${index + 1}]`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Auto move to previous input on backspace
    if (event.key === 'Backspace' && index > 0 && !value) {
      const prevInput = document.querySelector(`input[name=otp${index - 1}]`) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Auto verify when all digits are entered
    if (this.isOtpComplete()) {
      setTimeout(() => {
        this.verifyOtp();
      }, 500);
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text/plain').replace(/\D/g, '');
      if (pastedText.length === 5) {
        for (let i = 0; i < 5; i++) {
          this.otp[i] = pastedText.charAt(i);
        }
      }
    }
  }

  isOtpComplete(): boolean {
    return this.otp.every(digit => digit !== '');
  }

  calculatePasswordStrength() {
    const password = this.user.password;
    let strength = 0;

    // Check for lowercase
    this.hasLowerCase = /[a-z]/.test(password);
    if (this.hasLowerCase) strength += 25;

    // Check for uppercase
    this.hasUpperCase = /[A-Z]/.test(password);
    if (this.hasUpperCase) strength += 25;

    // Check for number
    this.hasNumber = /\d/.test(password);
    if (this.hasNumber) strength += 25;

    // Check for minimum length
    this.hasMinLength = password.length >= 8;
    if (this.hasMinLength) strength += 25;

    this.passwordStrength = strength;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  register() {
    if (!this.mobileVerified) {
      this.toast.error('Please verify your mobile number first');
      return;
    }

    // Validation
    if (!this.user.firstName || !this.user.lastName || !this.user.email || !this.user.password) {
      this.toast.error('Please fill all required fields');
      return;
    }

    // Set SmartSeller status
    if (this.wantsToBeSmartSeller) {
      this.user.isSmartSeller = this.wantsToBeSmartSeller;
    }

    this.isLoading = true;
    this.httpService.post(`store/register`, this.user).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.responseStatus.toLowerCase() === 'success') {
          const customer = response.response;
          this.common.setStorage('customer', customer);
          this.common.setLoggedIn(true);
          this.toast.success('Registration successful! Welcome to SmartSeller');
          this.router.navigate([this.common.getNavigteUrl()]);
        } else {
          this.toast.error(response.message || 'Registration failed');
        }
      },
      (error) => {
        this.isLoading = false;
        this.toast.error('Registration failed. Please try again.');
      }
    );
  }

  login(){
    this.router.navigateByUrl('/auth');
  }
}
